export enum CategoryIds {
    FabricsAndTrims = 101,
    Linings = 110,
}

export interface ExtractedFabricDetails {
    sourceUrl?: string | null;
    supplier?: string | null;
    fabricName?: string | null;
    itemNumberSku?: string | null;
    fabricWidthIn?: number | null;
    verticalRepeatIn?: number | null;
    horizontalRepeatIn?: number | null;
    dropRepeat?: boolean | null;
    contents?: string | null;
    minimumYardageRequirement?: string | null;
    imageUrl?: string | null;
    confidence?: Record<string, string>;
}

export interface FabricUrlImportPrefillOptions {
    categoryId: CategoryIds;
    supplierId?: number | null;
}

export interface FabricUrlTrackingOptions {
    sourceUrl?: string | null;
    orderType?: string;
    sectionName?: string;
    categoryId?: number;
}

const CATEGORY_NAMES: Record<CategoryIds, string> = {
    [CategoryIds.FabricsAndTrims]: "Fabrics & Trims",
    [CategoryIds.Linings]: "Linings",
};

const asText = (value: unknown): string => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim();
};

const asNumberText = (value: number | null | undefined): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return "";
    }
    return String(value);
};

const buildNotes = (details: ExtractedFabricDetails): string => {
    const lines = [
        details.contents ? `Contents: ${details.contents}` : "",
        details.minimumYardageRequirement
            ? `Minimum yardage requirement: ${details.minimumYardageRequirement}`
            : "",
    ].filter(Boolean);

    return lines.join("\n");
};

export const getUrlDomain = (sourceUrl?: string | null): string | undefined => {
    const rawUrl = asText(sourceUrl);
    if (!rawUrl) {
        return undefined;
    }

    try {
        return new URL(rawUrl).hostname;
    } catch (_error) {
        return undefined;
    }
};

export const buildFabricUrlImportPrefill = (
    details: ExtractedFabricDetails,
    options: FabricUrlImportPrefillOptions
) => {
    const categoryName = CATEGORY_NAMES[options.categoryId];
    const supplierId = options.supplierId || null;
    const prefill: Record<string, string | number | null> = {
        categoryId: options.categoryId,
        categoryName,
        name: asText(details.fabricName || details.itemNumberSku || "Imported Fabric"),
        itemNumber: asText(details.itemNumberSku),
        supplierName: asText(details.supplier),
        url: asText(details.sourceUrl),
        avatarId: asText(details.imageUrl),
        notes: buildNotes(details),
    };

    if (supplierId) {
        prefill.supplierId = supplierId;
    }

    return {
        prefill,
        detailsPrefill: {
            fabricWidthInches: asNumberText(details.fabricWidthIn),
            verticalRepeatInches: asNumberText(details.verticalRepeatIn),
            horizontalRepeatInches: asNumberText(details.horizontalRepeatIn),
            isDropRepeatOn: !!details.dropRepeat,
        },
    };
};

export const buildFabricUrlTrackingProps = ({
    sourceUrl,
    orderType,
    sectionName,
    categoryId,
}: FabricUrlTrackingOptions) => ({
    order_type: orderType || "",
    fabric_section: sectionName || "",
    fabric_category_id: categoryId,
    url_domain: getUrlDomain(sourceUrl) || "",
});
